// react
import React, { useState } from 'react';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { Icons } from '@/components/atoms/Icon/icons';
import { ExpandMore } from '@/components/atoms/ExpandMore/ExpandMore';

// mui
import {
  Box,
  CardContent,
  CardMedia,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { IconButtonProps } from '@mui/material/IconButton';

const DRAG_THRESHOLD_PX = 5;

export interface HorizontalCardProps {
  slot?: React.ReactNode;
  slotExpanded?: React.ReactNode;
  title: string;
  subtitle?: string;
  imageSrc?: string;
  icon?: Icons;
  onClick?: () => void;
  isCompact?: boolean;
  _isExpanded?: boolean;
}

/**
 * HorizontalCard component for displaying information in a horizontal card
 * layout.
 */
export const HorizontalCard = ({
  slot,
  slotExpanded,
  title,
  subtitle,
  imageSrc,
  icon,
  onClick,
  isCompact = false,
  _isExpanded,
}: HorizontalCardProps) => {
  // state
  const [mouseDownPos, setMouseDownPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [dragged, setDragged] = useState(false);
  const [isExpanded, setIsExanded] = useState(_isExpanded || false);
  const isExpandable = Boolean(slotExpanded);

  // hooks
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{ flex: 1, minWidth: 0, height: '100%', boxShadow: 'none' }}
    >
      <Stack
        direction='row'
        spacing={0}
        onMouseDown={(e) => {
          setMouseDownPos({ x: e.clientX, y: e.clientY });
          setDragged(false);
        }}
        onMouseMove={(e) => {
          if (!mouseDownPos) return;

          const dx = Math.abs(e.clientX - mouseDownPos.x);
          const dy = Math.abs(e.clientY - mouseDownPos.y);

          if (dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX) {
            setDragged(true);
          }
        }}
        onMouseUp={() => {
          if (!dragged) {
            onClick?.();
            if (isExpandable) {
              setIsExanded((prev) => !prev);
            }
          }
          setMouseDownPos(null);
        }}
        sx={{
          alignItems: 'stretch',
          cursor: onClick || isExpandable ? 'pointer' : 'default',
          userSelect: 'none',
          overflow: 'hidden',
        }}
      >
        {(imageSrc || icon) && (
          <Stack
            direction='row'
            spacing={0}
            sx={{
              width: isCompact ? 65 : 100,
              minHeight: 100,
              alignSelf: 'stretch',
              flexShrink: 0,
            }}
          >
            <Stack
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              {imageSrc && (
                <CardMedia
                  component='img'
                  image={imageSrc}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  color: alpha(theme.palette.text.primary, 0.3),
                }}
              >
                {icon && <Icon icon={icon} fontSize={30} />}
              </Box>
            </Stack>

            <Divider orientation='vertical' flexItem />
          </Stack>
        )}

        <Stack
          direction='column'
          spacing={1}
          sx={{
            paddingX: 2,
            paddingY: isCompact ? 1 : 2,
            flex: 1,
            justifyContent: 'center',
          }}
        >
          <Stack
            direction='row'
            spacing={1}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Stack direction='column' spacing={0}>
              <Typography variant='h2'>{title}</Typography>
              {subtitle && (
                <Typography variant='subtitle1'>{subtitle}</Typography>
              )}
            </Stack>
            {!isExpandable && onClick && (
              <IconButton sx={{ width: 32, height: 32 }}>
                <Icon icon='chevron-right' fontSize={16} />
              </IconButton>
            )}
            {isExpandable && (
              <ExpandMore
                isExpanded={isExpanded}
                aria-expanded={isExpanded}
                aria-label='show more'
              />
            )}
          </Stack>
          {slot && (
            <Stack direction='row' spacing={0}>
              {slot}
            </Stack>
          )}
        </Stack>
      </Stack>
      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        <Divider />
        <CardContent>{slotExpanded}</CardContent>
      </Collapse>
    </Paper>
  );
};

HorizontalCard.displayName = 'HorizontalCard';
export default HorizontalCard;
